/**
 * Flattens a hierarchical tree structure while maintaining parent-child order
 * @template T - The type of items in the hierarchy, must have a children property
 * @param {T[]} items - Array of hierarchical items with children property
 * @returns {Omit<T, 'children'>[]} - Flat array with children property removed, in hierarchical order (parent, then children)
 * @example
 * const hierarchy = [
 *   { id: 1, name: 'Parent', children: [
 *     { id: 2, name: 'Child', children: [] }
 *   ]}
 * ];
 * const flat = flattenHierarchy(hierarchy);
 * // Result: [{ id: 1, name: 'Parent' }, { id: 2, name: 'Child' }]
 */

export const flattenHierarchy = <T extends { children: T[] }>(items: T[]): Omit<T, 'children'>[] => {
  return items.reduce<Omit<T, 'children'>[]>((acc, item) => {
    const { children, ...itemData } = item;
    return [...acc, itemData, ...flattenHierarchy(children)];
  }, []);
};
